import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
    Calendar, 
    MapPin,
    Clock,
    Plus,
    Trash2,
    User,
    Edit2,
    X,
    Repeat,
    CalendarRange,
    CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format dates for display
const formatDateDisplay = (dates) => {
    if (!dates || dates.length === 0) return 'No date';
    if (dates.length === 1) {
        return new Date(dates[0] + 'T00:00:00').toLocaleDateString('en-US', { 
            month: 'short', day: 'numeric', year: 'numeric' 
        });
    }
    
    // Check if dates are consecutive
    const sortedDates = [...dates].sort();
    let isConsecutive = true;
    for (let i = 1; i < sortedDates.length; i++) {
        const prev = new Date(sortedDates[i-1]);
        const curr = new Date(sortedDates[i]);
        const diffDays = (curr - prev) / (1000 * 60 * 60 * 24);
        if (diffDays !== 1) {
            isConsecutive = false;
            break;
        }
    }
    
    if (isConsecutive && dates.length > 2) {
        // Show as range
        const first = new Date(sortedDates[0] + 'T00:00:00');
        const last = new Date(sortedDates[sortedDates.length - 1] + 'T00:00:00');
        return `${first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${last.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (dates.length <= 3) {
        // Show all dates
        return sortedDates.map(d => 
            new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        ).join(', ');
    } else {
        // Show first date + count
        const first = new Date(sortedDates[0] + 'T00:00:00');
        return `${first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} +${dates.length - 1} more`;
    }
};

const MemberEventsPage = () => {
    const { member } = useAuth();
    const [events, setEvents] = useState([]);
    const [myEvents, setMyEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Form state with multi-date support
    const [newEvent, setNewEvent] = useState({
        title: '',
        description: '',
        date: '',
        dates: [],
        time: '',
        location: '',
        event_type: 'hosting',
        dateMode: 'single', // single, multiple, range, recurring
        rangeEndDate: '',
        is_recurring: false,
        recurrence_type: 'weekly',
        recurrence_end_date: ''
    });
    const [editEvent, setEditEvent] = useState({
        title: '',
        description: '',
        date: '',
        dates: [],
        time: '',
        location: '',
        event_type: 'hosting',
        dateMode: 'single',
        rangeEndDate: '',
        is_recurring: false,
        recurrence_type: 'weekly',
        recurrence_end_date: ''
    });

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const [allEventsRes, myEventsRes] = await Promise.all([
                axios.get(`${API}/member/events`),
                axios.get(`${API}/member/events/mine`)
            ]);
            setEvents(allEventsRes.data);
            setMyEvents(myEventsRes.data);
        } catch (error) {
            console.error('Error fetching events:', error);
            toast.error('Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    // Generate dates based on mode
    const compileDates = (eventForm) => {
        let dates = [];
        
        if (eventForm.dateMode === 'single') {
            if (eventForm.date) dates = [eventForm.date];
        } else if (eventForm.dateMode === 'multiple') {
            dates = eventForm.dates || [];
        } else if (eventForm.dateMode === 'range') {
            // Generate all dates in range
            if (eventForm.date && eventForm.rangeEndDate) {
                const start = new Date(eventForm.date);
                const end = new Date(eventForm.rangeEndDate);
                const current = new Date(start);
                while (current <= end) {
                    dates.push(current.toISOString().split('T')[0]);
                    current.setDate(current.getDate() + 1);
                }
            }
        }
        // Recurring dates are handled by backend
        
        return dates;
    };

    const addDateToList = (formSetter, currentDates, newDate) => {
        if (newDate && !currentDates.includes(newDate)) {
            formSetter(prev => ({ ...prev, dates: [...currentDates, newDate].sort() }));
        }
    };

    const removeDateFromList = (formSetter, currentDates, dateToRemove) => {
        formSetter(prev => ({ ...prev, dates: currentDates.filter(d => d !== dateToRemove) }));
    };

    const handleSubmit = async () => {
        if (!newEvent.title.trim()) {
            toast.error('Please enter an event title');
            return;
        }
        
        // Validate dates based on mode
        if (newEvent.dateMode === 'single' && !newEvent.date) {
            toast.error('Please select a date');
            return;
        }
        if (newEvent.dateMode === 'multiple' && (!newEvent.dates || newEvent.dates.length === 0)) {
            toast.error('Please add at least one date');
            return;
        }
        if (newEvent.dateMode === 'range' && (!newEvent.date || !newEvent.rangeEndDate)) {
            toast.error('Please select both start and end dates');
            return;
        }
        if (newEvent.dateMode === 'recurring' && (!newEvent.date || !newEvent.recurrence_end_date)) {
            toast.error('Please select start date and end date for recurring event');
            return;
        }

        setIsSubmitting(true);
        try {
            const dates = compileDates(newEvent);
            const payload = {
                title: newEvent.title,
                description: newEvent.description,
                date: newEvent.date || (dates.length > 0 ? dates[0] : null),
                dates: newEvent.dateMode === 'recurring' ? null : dates,
                time: newEvent.time,
                location: newEvent.location,
                event_type: newEvent.event_type,
                is_recurring: newEvent.dateMode === 'recurring',
                recurrence_type: newEvent.dateMode === 'recurring' ? newEvent.recurrence_type : null,
                recurrence_end_date: newEvent.dateMode === 'recurring' ? newEvent.recurrence_end_date : null
            };
            
            await axios.post(`${API}/member/events`, payload);
            toast.success('Event shared successfully!');
            setShowAddModal(false);
            setNewEvent({
                title: '',
                description: '',
                date: '',
                dates: [],
                time: '',
                location: '',
                event_type: 'hosting',
                dateMode: 'single',
                rangeEndDate: '',
                is_recurring: false,
                recurrence_type: 'weekly',
                recurrence_end_date: ''
            });
            fetchEvents();
        } catch (error) {
            console.error('Error creating event:', error);
            toast.error(error.response?.data?.detail || 'Failed to share event');
        } finally {
            setIsSubmitting(false);
        }
    };

    const deleteEvent = async (eventId) => {
        if (!window.confirm('Are you sure you want to delete this event?')) return;
        
        try {
            await axios.delete(`${API}/member/events/${eventId}`);
            toast.success('Event deleted');
            fetchEvents();
        } catch (error) {
            console.error('Error deleting event:', error);
            toast.error('Failed to delete event');
        }
    };

    const openEditModal = (event) => {
        setEditingEvent(event);
        
        // Determine date mode based on existing data
        let dateMode = 'single';
        if (event.is_recurring) {
            dateMode = 'recurring';
        } else if (event.dates && event.dates.length > 1) {
            // Check if consecutive (range) or not (multiple)
            const sortedDates = [...event.dates].sort();
            let isConsecutive = true;
            for (let i = 1; i < sortedDates.length; i++) {
                const prev = new Date(sortedDates[i-1]);
                const curr = new Date(sortedDates[i]);
                const diffDays = (curr - prev) / (1000 * 60 * 60 * 24);
                if (diffDays !== 1) {
                    isConsecutive = false;
                    break;
                }
            }
            dateMode = isConsecutive ? 'range' : 'multiple';
        }
        
        setEditEvent({
            title: event.title,
            description: event.description || '',
            date: event.date || (event.dates && event.dates[0]) || '',
            dates: event.dates || [],
            time: event.time || '',
            location: event.location || '',
            event_type: event.event_type || 'hosting',
            dateMode: dateMode,
            rangeEndDate: dateMode === 'range' && event.dates ? event.dates[event.dates.length - 1] : '',
            is_recurring: event.is_recurring || false,
            recurrence_type: event.recurrence_type || 'weekly',
            recurrence_end_date: event.recurrence_end_date || ''
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            const dates = compileDates(editEvent);
            const payload = {
                title: editEvent.title,
                description: editEvent.description,
                date: editEvent.date || (dates.length > 0 ? dates[0] : null),
                dates: editEvent.dateMode === 'recurring' ? null : dates,
                time: editEvent.time,
                location: editEvent.location,
                event_type: editEvent.event_type,
                is_recurring: editEvent.dateMode === 'recurring',
                recurrence_type: editEvent.dateMode === 'recurring' ? editEvent.recurrence_type : null,
                recurrence_end_date: editEvent.dateMode === 'recurring' ? editEvent.recurrence_end_date : null
            };
            
            await axios.put(`${API}/member/events/${editingEvent.id}`, payload);
            toast.success('Event updated successfully');
            setShowEditModal(false);
            setEditingEvent(null);
            fetchEvents();
        } catch (error) {
            console.error('Error updating event:', error);
            toast.error('Failed to update event');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        // Parse date string to avoid timezone issues
        // Expected format: YYYY-MM-DD
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Helper to parse date parts without timezone issues
    const getDateParts = (dateString) => {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return {
            month: date.toLocaleDateString('en-US', { month: 'short' }),
            day: day
        };
    };

    const getEventTypeLabel = (type) => {
        switch (type) {
            case 'hosting': return 'Hosting';
            case 'attending': return 'Attending';
            case 'promoting': return 'Promoting';
            default: return type;
        }
    };

    const getEventTypeColor = (type) => {
        switch (type) {
            case 'hosting': return 'bg-[#C9A227] text-[#3D4435]';
            case 'attending': return 'bg-[#4A6B53] text-white';
            case 'promoting': return 'bg-[#5A6350] text-white';
            default: return 'bg-[#666] text-white';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-[#A0A0A0] animate-pulse">Loading events...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="member-events">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white font-['Playfair_Display'] flex items-center gap-3">
                        <Calendar className="w-8 h-8 text-[#C9A227]" />
                        Community Events
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">
                        Share and discover events in the community
                    </p>
                </div>
                <Button
                    onClick={() => setShowAddModal(true)}
                    className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                    data-testid="share-event-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Share an Event
                </Button>
            </div>

            {/* My Events */}
            {myEvents.length > 0 && (
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-white font-['Playfair_Display']">
                            My Shared Events
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {myEvents.map((event) => (
                                <div 
                                    key={event.id}
                                    className="flex items-center gap-4 p-4 bg-[#3D4435] rounded-xl border border-[#5A6350]"
                                >
                                    <div className="w-14 h-14 rounded-lg bg-[#C9A227]/10 flex flex-col items-center justify-center text-[#C9A227] flex-shrink-0">
                                        {event.dates && event.dates.length > 1 ? (
                                            <>
                                                <CalendarDays className="w-5 h-5 mb-0.5" />
                                                <span className="text-xs font-medium">{event.dates.length} dates</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-xs font-medium">
                                                    {getDateParts(event.date).month}
                                                </span>
                                                <span className="text-xl font-bold leading-none">
                                                    {getDateParts(event.date).day}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="text-white font-medium">{event.title}</h3>
                                            <span className={`px-2 py-0.5 rounded text-xs ${getEventTypeColor(event.event_type)}`}>
                                                {getEventTypeLabel(event.event_type)}
                                            </span>
                                            {event.is_recurring && (
                                                <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-300 flex items-center gap-1">
                                                    <Repeat className="w-3 h-3" />
                                                    {event.recurrence_type}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-3 text-sm text-[#A0A0A0]">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {formatDateDisplay(event.dates && event.dates.length > 0 ? event.dates : [event.date])}
                                            </span>
                                            {event.time && (
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {event.time}
                                                </span>
                                            )}
                                            {event.location && (
                                                <span className="flex items-center gap-1">
                                                    <MapPin className="w-3 h-3" />
                                                    {event.location}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-[#C9A227] hover:text-[#E8D48A] hover:bg-[#C9A227]/10"
                                            onClick={() => openEditModal(event)}
                                            data-testid={`edit-event-${event.id}`}
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                                            onClick={() => deleteEvent(event.id)}
                                            data-testid={`delete-event-${event.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* All Community Events */}
            <Card className="bg-[#4A5240] border-[#5A6350]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-white font-['Playfair_Display']">
                        All Community Events
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {events.length === 0 ? (
                        <div className="text-center py-12">
                            <Calendar className="w-12 h-12 text-[#666] mx-auto mb-4" />
                            <h3 className="text-white font-semibold mb-2">No events yet</h3>
                            <p className="text-[#A0A0A0] text-sm mb-4">
                                Be the first to share an event with the community!
                            </p>
                            <Button
                                onClick={() => setShowAddModal(true)}
                                className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Share an Event
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {events.map((event) => (
                                <div 
                                    key={event.id}
                                    className="p-5 bg-[#3D4435] rounded-xl border border-[#5A6350]"
                                    data-testid={`event-${event.id}`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-16 h-16 rounded-lg bg-[#C9A227]/10 flex flex-col items-center justify-center text-[#C9A227] flex-shrink-0">
                                            {event.dates && event.dates.length > 1 ? (
                                                <>
                                                    <CalendarDays className="w-6 h-6 mb-0.5" />
                                                    <span className="text-xs font-medium">{event.dates.length} dates</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="text-xs font-medium">
                                                        {getDateParts(event.date).month}
                                                    </span>
                                                    <span className="text-2xl font-bold leading-none">
                                                        {getDateParts(event.date).day}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <h3 className="text-white font-semibold text-lg">{event.title}</h3>
                                                <span className={`px-2 py-0.5 rounded text-xs ${getEventTypeColor(event.event_type)}`}>
                                                    {getEventTypeLabel(event.event_type)}
                                                </span>
                                                {event.is_recurring && (
                                                    <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-300 flex items-center gap-1">
                                                        <Repeat className="w-3 h-3" />
                                                        {event.recurrence_type}
                                                    </span>
                                                )}
                                            </div>
                                            {event.description && (
                                                <p className="text-[#A0A0A0] text-sm mb-3">{event.description}</p>
                                            )}
                                            <div className="flex flex-wrap gap-4 text-sm text-[#A0A0A0]">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4" />
                                                    {formatDateDisplay(event.dates && event.dates.length > 0 ? event.dates : [event.date])}
                                                </span>
                                                {event.time && (
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-4 h-4" />
                                                        {event.time}
                                                    </span>
                                                )}
                                                {event.location && (
                                                    <span className="flex items-center gap-1">
                                                        <MapPin className="w-4 h-4" />
                                                        {event.location}
                                                    </span>
                                                )}
                                            </div>
                                            {event.member && (
                                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#5A6350]">
                                                    {event.member.profile_image ? (
                                                        <img 
                                                            src={event.member.profile_image} 
                                                            alt={event.member.name}
                                                            className="w-6 h-6 rounded-full object-cover"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextElementSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div className={`w-6 h-6 rounded-full bg-[#5A6350] items-center justify-center text-[#A0A0A0] text-xs ${event.member.profile_image ? 'hidden' : 'flex'}`}>
                                                        {event.member.name?.charAt(0)}
                                                    </div>
                                                    <span className="text-[#666] text-sm">
                                                        Shared by {event.member.name}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Add Event Modal */}
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogContent className="bg-[#4A5240] border-[#5A6350] text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-['Playfair_Display']">Share an Event</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-white">Event Title *</Label>
                            <Input
                                value={newEvent.title}
                                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                                placeholder="e.g., Networking Meetup"
                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                data-testid="event-title-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white">Description</Label>
                            <Textarea
                                value={newEvent.description}
                                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                                placeholder="What's this event about?"
                                rows={3}
                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                data-testid="event-description-input"
                            />
                        </div>
                        
                        {/* Date Mode Selection */}
                        <div className="space-y-2">
                            <Label className="text-white">Date Type</Label>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { mode: 'single', label: 'Single Date', icon: Calendar },
                                    { mode: 'multiple', label: 'Multiple Dates', icon: CalendarDays },
                                    { mode: 'range', label: 'Date Range', icon: CalendarRange },
                                    { mode: 'recurring', label: 'Recurring', icon: Repeat }
                                ].map(({ mode, label, icon: Icon }) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => setNewEvent({ ...newEvent, dateMode: mode, dates: [], rangeEndDate: '' })}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                                            newEvent.dateMode === mode
                                                ? 'bg-[#C9A227] text-[#3D4435]'
                                                : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Single Date */}
                        {newEvent.dateMode === 'single' && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white">Date *</Label>
                                    <Input
                                        type="date"
                                        value={newEvent.date}
                                        onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                                        className="bg-[#3D4435] border-[#5A6350] text-white"
                                        data-testid="event-date-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white">Time</Label>
                                    <Input
                                        type="time"
                                        value={newEvent.time}
                                        onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                                        className="bg-[#3D4435] border-[#5A6350] text-white"
                                        data-testid="event-time-input"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Multiple Dates */}
                        {newEvent.dateMode === 'multiple' && (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-white">Add Dates *</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                type="date"
                                                id="newDateInput"
                                                className="bg-[#3D4435] border-[#5A6350] text-white"
                                            />
                                            <Button
                                                type="button"
                                                onClick={() => {
                                                    const input = document.getElementById('newDateInput');
                                                    if (input.value) {
                                                        addDateToList(setNewEvent, newEvent.dates, input.value);
                                                        input.value = '';
                                                    }
                                                }}
                                                className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white">Time</Label>
                                        <Input
                                            type="time"
                                            value={newEvent.time}
                                            onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                                            className="bg-[#3D4435] border-[#5A6350] text-white"
                                        />
                                    </div>
                                </div>
                                {newEvent.dates.length > 0 && (
                                    <div className="flex flex-wrap gap-2 p-3 bg-[#3D4435] rounded-lg">
                                        {newEvent.dates.map((d) => (
                                            <Badge key={d} className="bg-[#C9A227] text-[#3D4435] flex items-center gap-1">
                                                {new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                <button
                                                    type="button"
                                                    onClick={() => removeDateFromList(setNewEvent, newEvent.dates, d)}
                                                    className="hover:text-red-800"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Date Range */}
                        {newEvent.dateMode === 'range' && (
                            <div className="space-y-3">
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="space-y-2">
                                        <Label className="text-white">Start Date *</Label>
                                        <Input
                                            type="date"
                                            value={newEvent.date}
                                            onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                                            className="bg-[#3D4435] border-[#5A6350] text-white"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white">End Date *</Label>
                                        <Input
                                            type="date"
                                            value={newEvent.rangeEndDate}
                                            onChange={(e) => setNewEvent({ ...newEvent, rangeEndDate: e.target.value })}
                                            min={newEvent.date}
                                            className="bg-[#3D4435] border-[#5A6350] text-white"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white">Time</Label>
                                        <Input
                                            type="time"
                                            value={newEvent.time}
                                            onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                                            className="bg-[#3D4435] border-[#5A6350] text-white"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Recurring */}
                        {newEvent.dateMode === 'recurring' && (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-2">
                                        <Label className="text-white">Start Date *</Label>
                                        <Input
                                            type="date"
                                            value={newEvent.date}
                                            onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                                            className="bg-[#3D4435] border-[#5A6350] text-white"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white">Repeat Until *</Label>
                                        <Input
                                            type="date"
                                            value={newEvent.recurrence_end_date}
                                            onChange={(e) => setNewEvent({ ...newEvent, recurrence_end_date: e.target.value })}
                                            min={newEvent.date}
                                            className="bg-[#3D4435] border-[#5A6350] text-white"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-2">
                                        <Label className="text-white">Repeat</Label>
                                        <div className="flex gap-2">
                                            {['weekly', 'biweekly', 'monthly'].map((type) => (
                                                <button
                                                    key={type}
                                                    type="button"
                                                    onClick={() => setNewEvent({ ...newEvent, recurrence_type: type })}
                                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                                        newEvent.recurrence_type === type
                                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                                    }`}
                                                >
                                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white">Time</Label>
                                        <Input
                                            type="time"
                                            value={newEvent.time}
                                            onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                                            className="bg-[#3D4435] border-[#5A6350] text-white"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label className="text-white">Location</Label>
                            <Input
                                value={newEvent.location}
                                onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                                placeholder="e.g., Washington, DC"
                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                data-testid="event-location-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white">Event Type</Label>
                            <div className="flex flex-wrap gap-2">
                                {['hosting', 'attending', 'promoting'].map((type) => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => setNewEvent({ ...newEvent, event_type: type })}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                            newEvent.event_type === type
                                                ? 'bg-[#C9A227] text-[#3D4435]'
                                                : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                        }`}
                                        data-testid={`event-type-${type}`}
                                    >
                                        {getEventTypeLabel(type)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setShowAddModal(false)}
                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                            data-testid="submit-event-btn"
                        >
                            {isSubmitting ? 'Sharing...' : 'Share Event'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Event Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="bg-[#4A5240] border-[#5A6350] text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                            <Edit2 className="w-5 h-5 text-[#C9A227]" />
                            Edit Event
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEditSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-white">Event Title *</Label>
                            <Input
                                placeholder="Event title"
                                value={editEvent.title}
                                onChange={(e) => setEditEvent({ ...editEvent, title: e.target.value })}
                                required
                                className="bg-[#3D4435] border-[#5A6350] text-white"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-white">Description</Label>
                            <Textarea
                                placeholder="Tell us about this event..."
                                value={editEvent.description}
                                onChange={(e) => setEditEvent({ ...editEvent, description: e.target.value })}
                                className="bg-[#3D4435] border-[#5A6350] text-white min-h-[80px]"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white">Date *</Label>
                                <Input
                                    type="date"
                                    value={editEvent.date}
                                    onChange={(e) => setEditEvent({ ...editEvent, date: e.target.value })}
                                    required
                                    className="bg-[#3D4435] border-[#5A6350] text-white"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white">Time</Label>
                                <Input
                                    type="time"
                                    value={editEvent.time}
                                    onChange={(e) => setEditEvent({ ...editEvent, time: e.target.value })}
                                    className="bg-[#3D4435] border-[#5A6350] text-white"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-white">Location</Label>
                            <Input
                                placeholder="Event location"
                                value={editEvent.location}
                                onChange={(e) => setEditEvent({ ...editEvent, location: e.target.value })}
                                className="bg-[#3D4435] border-[#5A6350] text-white"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-white">Event Type</Label>
                            <div className="flex flex-wrap gap-2">
                                {['hosting', 'attending', 'promoting'].map((type) => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => setEditEvent({ ...editEvent, event_type: type })}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                            editEvent.event_type === type
                                                ? 'bg-[#C9A227] text-[#3D4435]'
                                                : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                        }`}
                                    >
                                        {getEventTypeLabel(type)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <DialogFooter className="gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowEditModal(false)}
                                className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                            >
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MemberEventsPage;
